/**
 * Logout automático por inatividade (24h).
 *
 * Estratégia: grava um heartbeat `lastActiveAt` em localStorage sempre
 * que o app estiver ativo (bootstrap com sessão, visibilitychange visible,
 * focus, online, pageshow). No boot / retomada, se o intervalo desde o
 * último heartbeat exceder `INACTIVITY_MAX_MS`, a sessão é derrubada
 * localmente via `tearDownLocalSession` — mesmo caminho de um refresh
 * falho, então o usuário vê o banner "Sua sessão expirou".
 *
 * Não afeta o token access (renovado normalmente pelo supabase-js).
 * Só força re-login quando o app ficou ≥24h sem ser aberto.
 */

const KEY = "zappy.session.last-active-at";
export const INACTIVITY_MAX_MS = 24 * 60 * 60 * 1000; // 24h

const getStorage = (): Storage | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

export const touchLastActive = (): void => {
  const s = getStorage();
  if (!s) return;
  try {
    s.setItem(KEY, String(Date.now()));
  } catch {
    /* noop */
  }
};

export const getLastActive = (): number | null => {
  const s = getStorage();
  if (!s) return null;
  try {
    const raw = s.getItem(KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
};

export const clearLastActive = (): void => {
  const s = getStorage();
  if (!s) return;
  try {
    s.removeItem(KEY);
  } catch {
    /* noop */
  }
};

/**
 * Logout automático por inatividade foi DESATIVADO por decisão de produto:
 * a sessão deve durar 24h (ou mais) enquanto o refresh token do Supabase
 * for válido, sem forçar re-login só porque o app ficou fechado.
 *
 * Mantemos o heartbeat (`touchLastActive`) apenas como telemetria; a
 * checagem sempre retorna `false` para nunca derrubar a sessão local.
 */
export const isInactivityExpired = (): boolean => {
  return false;
};
